import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { isNull, assign } from 'lodash';

import { Spinner } from '~/components';
import { Input, Autocomplete, Hidden, Select } from '~/components/Form';
import { t } from '~/helpers';
import { storeBico, updateBico } from '~/store/modules/bico/actions';
import { SelectComplete } from '~/componentslocal';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const [posto, setPosto] = useState({ poco_codigo: '', poco_descricao: '' });
  const [eletronica, setEletronica] = useState(false);

  const [tavi, setTavi] = useState({ tavi_codigo: '' })
  const [term, setTerm] = useState({ term_identificacao: '' })

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.bico);

  const initials = create ? data : { ...find, ...data };

  const optionSN = useMemo(
    () => [
      { value: 'S', label: 'Sim' },
      { value: 'N', label: 'Não' },
    ],
    []
  );

  const optionStatus = useMemo(
    () => [
      { value: 1, label: 'Liberado' },
      { value: 2, label: 'Bloqueado' },
      { value: 3, label: 'Cancelado' },
    ],
    []
  );

  const handleSetValue = useCallback((field, value) => {
    reference.current.setFieldValue(field, value);
  }, []);

  const handleSubmit = useCallback(
    async (param) => {
      try {
        const schema = Yup.object().shape({
          poco_codigo: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .nullable(),
          tavi_codigo: Yup.string()
            .nullable()
            .required(t('s.geral.validacao.campoobrigatorio')),
          term_identificacao: Yup.string()
            .nullable()
            .required(t('s.geral.validacao.campoobrigatorio')),
          medi_codigo: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          bico_numero: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
            .max(999, t('s.geral.validacao.tamanhomaximo').replace('%s', 999)),
          bico_descricao: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(2, t('s.geral.validacao.tamanhominimo').replace('%s', 2))
            .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30)),
          bico_timeout: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
            .max(999, t('s.geral.validacao.tamanhomaximo').replace('%s', 999)),
          bico_timeout_vazamento: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
            .max(999, t('s.geral.validacao.tamanhomaximo').replace('%s', 999)),
          bico_tempo_liga_bomba: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
            .max(999, t('s.geral.validacao.tamanhomaximo').replace('%s', 999)),
          bico_percentual_variacao: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 0))
            .max(100, t('s.geral.validacao.tamanhomaximo').replace('%s', 100)),
          bico_vazao_nominal: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 0))
            .max(
              100000,
              t('s.geral.validacao.tamanhomaximo').replace('%s', 100000)
            ),
          bico_fator_k: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 0))
            .max(
              1000,
              t('s.geral.validacao.tamanhomaximo').replace('%s', 1000)
            ),
          bico_bomba_eletronica: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          bico_canal_CBC: eletronica
            ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 0))
              .max(7, t('s.geral.validacao.tamanhomaximo').replace('%s', 7))
            : Yup.string().nullable(),
          bico_endereco_CBC: eletronica
            ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 0))
              .max(
                999,
                t('s.geral.validacao.tamanhomaximo').replace('%s', 999)
              )
            : Yup.string().nullable(),
          bico_num_lado_CBC: eletronica
            ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 0))
              .max(3, t('s.geral.validacao.tamanhomaximo').replace('%s', 3))
            : Yup.string().nullable(),
          bico_num_cs_total_apagar: eletronica
            ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 0))
              .max(3, t('s.geral.validacao.tamanhomaximo').replace('%s', 3))
            : Yup.string().nullable(),
          bico_num_cs_volume: eletronica
            ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 0))
              .max(3, t('s.geral.validacao.tamanhomaximo').replace('%s', 3))
            : Yup.string().nullable(),
          bico_num_cs_preco_por_litro: eletronica
            ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 0))
              .max(3, t('s.geral.validacao.tamanhomaximo').replace('%s', 3))
            : Yup.string().nullable(),
          bico_preco_produto_CBC: eletronica
            ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .min(0, t('s.geral.validacao.tamanhominimo').replace('%s', 0))
              .max(
                9999,
                t('s.geral.validacao.tamanhomaximo').replace('%s', 9999)
              )
            : Yup.string().nullable(),
          bico_exibe_volume: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          bico_status: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
        });

        await schema.validate(param, {
          abortEarly: false,
        });

        const objSend = {
          ...initials,
          ...param,
          bico_bomba_eletronica: eletronica ? 'S' : 'N',
          idcliente: client,
        };

        if (create) dispatch(storeBico(objSend, params));
        else dispatch(updateBico(objSend, params));
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          reference.current.setErrors(validationErrors);
        }
      }
    },
    [eletronica, client, create]
  );

  // const handleChangePost = useCallback(
  //   (value) => {
  //     if (create) {
  //       // reference.current.clearField('tavi_codigo');
  //       // reference.current.clearField('term_identificacao');
  //       // reference.current.clearField('term_descricao');
  //     }
  //     setPosto(value);
  //   },
  //   [create]
  // );


  useEffect(() => {
    setTerm({ term_identificacao: '' })
    setTavi({ tavi_codigo: '' })
  }, [posto])


  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form
          ref={reference}
          initialData={assign(
            {
              bico_bomba_eletronica: 'N',
              bico_exibe_volume: 'N',
              bico_status: 1,
            },
            initials
          )}
          onSubmit={handleSubmit}
        >
          <Autocomplete
            name="poco_codigo"
            label="poco_descricao"
            title="Posto"
            url="postoscomboios"
            unique="poco_codigo"
            disabled={!create}
            hiddens={[{ name: 'poco_descricao' }]}
            getValue={(value) => setPosto(value)}
          />


            <SelectComplete
              includeInputInList
              disabledItemsFocusable
              name="tavi_codigo"
              label="tavi_nome"
              title="Tanque Virtual"
              url="tanquevirtual"
              unique="tavi_codigo"
              disabled={posto !== undefined ? !posto.poco_codigo : true}
              // disabled={undefined!posto.poco_codigo}
              disableClearable={!tavi.tavi_codigo}
              params={{
                wherefield: 'poco_codigo',
                valuewherefield: posto !== undefined ? posto.poco_codigo : '',
                // valuewherefield: posto.poco_codigo,
                typewherefield: 'EQUALS',
              }}
              inputValue={tavi.tavi_codigo}
              onInputChange={(_, value) =>
                setTavi({ tavi_codigo: value })
              }
            />


            <Autocomplete
              includeInputInList
              disabledItemsFocusable
              name="term_identificacao"
              label="term_descricao"
              title="Terminal"
              url="terminal"
              unique="term_identificacao"
              disabled={posto !== undefined ? !posto.poco_codigo : true}
              // disabled={!posto.poco_codigo}
              disableClearable={!term.term_identificacao}
              params={{
                wherefield: 'poco_codigo',
                valuewherefield: posto !== undefined ? posto.poco_codigo : '',
                typewherefield: 'EQUALS',
              }}
              getValue={({ term_descricao }) => {
                if (!isNull(term_descricao))
                  handleSetValue('term_descricao', term_descricao);
              }}
              inputValue={term.term_identificacao}
              onInputChange={(_, value) =>
                setTerm({ term_identificacao: value })
              }
            />


          <Hidden name="term_descricao" />

          <Autocomplete
            name="medi_codigo"
            label="medi_descricao"
            title="Medidor"
            url="medidores"
            unique="medi_codigo"
            hiddens={[{ name: 'medi_descricao' }]}
          />

          <Input
            name="bico_numero"
            type="number"
            label="Número"
            disabled={!create}
          />

          <Input name="bico_descricao" label="Descrição" />

          <Input name="bico_timeout" type="number" label="TimeOut" />

          <Input
            name="bico_timeout_vazamento"
            type="number"
            label="TimeOut Vazamento"
          />

          <Input
            name="bico_tempo_liga_bomba"
            type="number"
            label="Tempo Liga Bomba"
          />

          <Input
            name="bico_percentual_variacao"
            type="number"
            label="% Variação"
          />

          <Input
            name="bico_vazao_nominal"
            type="number"
            label="Vazão Nominal"
          />

          <Input name="bico_fator_k" type="number" label="Fator K" />

          <Select
            name="bico_bomba_eletronica"
            label="Bomba Eletrônica"
            options={optionSN}
            getValue={(value) => setEletronica(value === 'S')}
          />

          <Input
            name="bico_canal_CBC"
            label="Canal CBC"
            type="number"
            disabled={!eletronica}
          />

          <Input
            name="bico_endereco_CBC"
            label="Endereço CBC"
            type="number"
            disabled={!eletronica}
          />

          <Input
            name="bico_num_lado_CBC"
            type="number"
            label="Bicos por lado CBC"
            disabled={!eletronica}
          />

          <Input
            name="bico_num_cs_total_apagar"
            type="number"
            label="Número Casas Total Apagar (0-3)"
            disabled={!eletronica}
          />

          <Input
            name="bico_num_cs_volume"
            type="number"
            label="Número Casas Volume (0-3)"
            disabled={!eletronica}
          />

          <Input
            name="bico_num_cs_preco_por_litro"
            type="number"
            label="Número Casas Preço Por Litro (0-3)"
            disabled={!eletronica}
          />

          <Input
            name="bico_preco_produto_CBC"
            type="number"
            label="Preço Produto Bico (x1000)"
            disabled={!eletronica}
          />

          <Select
            name="bico_exibe_volume"
            label="Exibe Vol. Diagnóstico"
            options={optionSN}
          />

          <Select name="bico_status" label="Status" options={optionStatus} />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
